/** Pure helpers backing the creator-sheet import wizard's steps. No React,
 *  no server calls — testable in isolation. */
import type {
  CreatorNote,
  ExpectedField,
  ImportBatchRow,
  ImportFieldGroup,
  ImportResult,
  OutreachImportBatch,
  Platform,
  RosterResolutionAction,
} from '@discovr/types';
import { creatorEditGroupLabel, isOfferedOnPlatform } from '@discovr/types';
import { parseAmount } from './amountParse';
import { stripAllInvisibleChars, trimSpace } from './invisibleChars';
import { MAX_USAGE_RIGHTS_DAYS, MIN_USAGE_RIGHTS_DAYS } from './importLimits';
import { isBlockingStatus, isDroppedRow, reviewRowStatus } from './reviewRowStatus';

// ── Shared by every wizard file ───────────────────────────────────────────
// One definition each: a renamed key or a changed rule is edited here only.
export const USERNAME_KEY = 'username';
export const USAGE_RIGHTS_KEY = 'usage_rights';
export const USAGE_RIGHTS_DAYS_KEY = 'usage_rights_days';
export const MAIN_DELIVERABLE_GROUP: ImportFieldGroup = 'main';

export function isBlank(value: string | undefined): boolean {
  return (value ?? '').trim() === '';
}

/** The word that fits the count: pluralWord(1, 'row', 'rows') -> "row". */
export function pluralWord(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/** The count with its word: countLabel(2, 'row', 'rows') -> "2 rows". */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${pluralWord(count, singular, plural)}`;
}

/** For a wizard action whose failure the wizard hook has already put in its
 *  own error state before rethrowing: the rejection only needs to stop being
 *  "unhandled". */
export function ignoreSurfacedError(): void {}

// ── Sheet-preview column sizing ───────────────────────────────────────────
// The client-side preview table (ImportUploadStep) renders whatever raw
// header text is in the uploaded sheet — this runs before mapping, so we
// don't yet know which BE field a column will become. `estimatePreviewColumnWidths`
// still gives each column a sensible max-width by combining three signals:
// (1) the header text itself (a column is never narrower than its own
// label), (2) this app's own field-length limits where the header happens to
// match a known field key (e.g. first_name/last_name cap at 100 chars — see
// maxNameLength in the BE's import_review_rules.go), and (3) a conventional
// spreadsheet sizing fallback by inferred type (numeric columns stay narrow,
// free text gets more room) for anything unrecognized. A hard ceiling keeps
// any one column (a maxed-out 100-char name, a very long unmatched header)
// from dominating the table — real overflow still shows via truncation +
// a title tooltip, not a runaway-wide column.
const KNOWN_FIELD_MAX_CHARS: Record<string, number> = {
  username: 30, // Instagram/YouTube handle length convention
  first_name: 100,
  last_name: 100,
  engagement_rate: 8, // e.g. "12.34%"
  avg_views: 12, // e.g. "999,999,999"
};
const NUMERIC_MAX_CHARS = 12;
const TEXT_MAX_CHARS = 40;
const ABSOLUTE_MAX_CHARS = 40;
const MIN_CHARS = 6;
const CHAR_PX = 8; // ~average glyph width for the preview table's 14px text
const CELL_PADDING_PX = 24; // px-3 on both sides
const MIN_COLUMN_PX = 72;
const MAX_COLUMN_PX = 320;

function normalizeHeaderKey(header: string): string {
  return header.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function looksNumeric(value: string): boolean {
  return value === '' || /^-?[\d,.]+%?$/.test(value);
}

function maxCharsForColumn(header: string, values: string[]): number {
  const known = KNOWN_FIELD_MAX_CHARS[normalizeHeaderKey(header)];
  if (known !== undefined) return known;
  return values.length > 0 && values.every(looksNumeric) ? NUMERIC_MAX_CHARS : TEXT_MAX_CHARS;
}

/** One max-width in px per column, in header order. */
export function estimatePreviewColumnWidths(rows: string[][]): number[] {
  const header = rows[0] ?? [];
  return header.map((headerText, colIndex) => {
    const values = rows.slice(1).map((row) => row[colIndex] ?? '');
    const maxChars = maxCharsForColumn(headerText, values);
    const longestValueChars = values.reduce((max, v) => Math.max(max, v.length), 0);
    const chars = Math.min(
      Math.max(headerText.length, Math.min(longestValueChars, maxChars)),
      ABSOLUTE_MAX_CHARS,
    );
    const px = Math.max(chars, MIN_CHARS) * CHAR_PX + CELL_PADDING_PX;
    return Math.min(Math.max(px, MIN_COLUMN_PX), MAX_COLUMN_PX);
  });
}

/** Shortens a machine field key to the recommended spelling used in the
 *  downloadable template's header row. Every shortened spelling is one the
 *  BE's own
 *  fieldKeywords/platformShortAlias already accepts
 *  (admin_import_preview_handler.go / admin_handler.go), so a sheet built
 *  from our own template round-trips through mapping correctly:
 *  - engagement_rate -> eng_rate
 *  - any instagram_*  -> ig_*   (mirrors platformShortAlias)
 *  - any youtube_*    -> yt_*   (mirrors platformShortAlias)
 */
function compactKey(key: string): string {
  const normalized = key.trim().toLowerCase();
  if (normalized === 'engagement_rate') return 'eng_rate';
  if (normalized.startsWith('instagram_')) return `ig_${key.slice('instagram_'.length)}`;
  if (normalized.startsWith('youtube_')) return `yt_${key.slice('youtube_'.length)}`;
  return key;
}

/** A field's machine key as written in the downloadable template's header
 *  row — see `compactKey`. */
export function compactFieldKey(f: ExpectedField): string {
  return compactKey(f.key);
}

/** Fields the review grid's current-vs-sheet comparison can never
 *  meaningfully show a "current" value for:
 *  - username — the handle that matched the row, identical on both sides by
 *    construction (it's what matched the row).
 *  - first_name/last_name — these live on the creator's User record, not on
 *    the roster entry itself, and nothing in the import review fetches
 *    it (a deliberate scope call, not an oversight — see the BE's
 *    rosterEntryCurrentValues). Showing "— → John" here would look like a
 *    real change when it's actually just a value we never had. */
const NOT_COMPARABLE_FIELDS = new Set([USERNAME_KEY, 'first_name', 'last_name']);

/** The stats a frozen creator keeps whatever the sheet says (RosterMatch.statsFrozen). */
const STAT_FIELD_KEYS = new Set(['engagement_rate', 'avg_views', 'followers']);

export function isStatField(f: ExpectedField): boolean {
  return STAT_FIELD_KEYS.has(f.key);
}

/** A locked match's prices AND its usage-rights days are ignored at commit
 *  (the BE's rosterMatchIsIdentical compares only stat fields there). */
export function isFrozenOnLockedMatch(f: ExpectedField): boolean {
  return Boolean(f.isDeliverable) || f.key === USAGE_RIGHTS_DAYS_KEY;
}

/** Which fields the review grid compares between the campaign and the sheet
 *  on a roster-matched row.
 *  - See NOT_COMPARABLE_FIELDS above.
 *  - Deliverable prices are excluded when the matched entry is locked: an
 *    Overwrite there never touches them (RosterMatch.locked), so listing
 *    them as a "current vs new" pair would show a comparison that can't
 *    actually happen — stat fields always update regardless of lock state.
 *  - Stat fields are excluded when the creator's stats are frozen: the import
 *    writes none of them, so the sheet's numbers are not a choice to make.
 *  - Only fields the SHEET carries a value for are shown. A column the sheet
 *    never had, or a cell left blank, writes nothing at commit (a blank price
 *    preserves the deliverable; a blank optional stat leaves the fetched one
 *    alone) — so listing "543000 → —" would describe a change that can't
 *    happen and make every fetched creator look like a conflict on a
 *    re-import. The sheet filling in a previously-blank roster value still
 *    shows, since that IS a real change. Mirrors the BE's
 *    rosterMatchIsIdentical. */
export function rosterComparisonFields(
  expectedFields: ExpectedField[],
  locked: boolean,
  newValues: Record<string, string>,
  statsFrozen = false,
): ExpectedField[] {
  return expectedFields.filter((f) => {
    if (NOT_COMPARABLE_FIELDS.has(f.key) || (locked && isFrozenOnLockedMatch(f))) return false;
    if (statsFrozen && isStatField(f)) return false;
    return Boolean(newValues[f.key]);
  });
}

/** The mapping step's "First value" preview, cleaned the same way the import
 *  will actually store the cell — so the admin sees the real result the moment
 *  they pick a field, not the raw sheet text:
 *  - username: a leading "@" is dropped (mirrors the BE's NormalizeSocialHandle).
 *  - deliverable price: parsed with the same shared parseAmount everything
 *    else in this file uses, so "₹5,000", "12k", and "₮5,000" all preview as
 *    their expanded numeric form ("5000", "12000", "5000"). A cell parseAmount
 *    can't take (a range, a date, free text) is shown as-is — import skips it
 *    rather than cleaning it.
 *  - any other field, or an unmapped column: the raw value, unchanged. */
export function cleanedFirstValue(fieldKey: string, isDeliverable: boolean, raw: string): string {
  const value = raw.trim();
  if (!fieldKey || value === '') return raw;
  if (fieldKey === USERNAME_KEY) return value.replace(/^@+/, '');
  if (isDeliverable) {
    const n = parseAmount(value, 'price');
    if (n === null) return raw;
    // Round to at most 2 decimal places (paise-level precision — the finest
    // a deliverable price is ever stored at) before stringifying. A
    // decimal-suffix input (e.g. "1.1 lakh" -> 1.1 * 100000) can land on an
    // IEEE-754 artifact like 110000.00000000001 in floating-point
    // arithmetic — String()'ing that directly would show the ugly float
    // instead of the clean whole number the admin expects. Rounding first
    // clears the float noise; JS numbers drop trailing zeros on their own
    // (String(110000) is "110000", not "110000.00"), so no separate
    // zero-stripping step is needed.
    return String(Math.round(n * 100) / 100);
  }
  return raw;
}

export interface MappingClash {
  header: string;
  fieldKey: string;
  otherHeaders: string[];
}

/** Columns that share one field. Clashes are shown, not prevented (a field
 *  can sit in the dropdown of every column), so each involved column is
 *  reported with the others it collides with. Unmapped columns never clash. */
export function mappingClashes(mapping: Record<string, string>): MappingClash[] {
  const headersByField = new Map<string, string[]>();
  for (const [header, fieldKey] of Object.entries(mapping)) {
    if (!fieldKey) continue;
    headersByField.set(fieldKey, [...(headersByField.get(fieldKey) ?? []), header]);
  }
  return Object.entries(mapping).flatMap(([header, fieldKey]): MappingClash[] => {
    const sharing = headersByField.get(fieldKey) ?? [];
    if (!fieldKey || sharing.length < 2) return [];
    return [{ header, fieldKey, otherHeaders: sharing.filter((h) => h !== header) }];
  });
}

const FIELD_GROUP_ORDER: readonly ImportFieldGroup[] = ['creator', 'audience', 'main', 'addon'];

/** Dropdown options grouped like the Edit creator drawer. Fields keep the
 *  server's order inside a group; groups with no field are left out. */
export function groupedFieldOptions(
  expectedFields: ExpectedField[],
  platform: Platform,
): { group: ImportFieldGroup; label: string; fields: ExpectedField[] }[] {
  return FIELD_GROUP_ORDER
    .map((group) => ({
      group,
      label: creatorEditGroupLabel(group, platform),
      fields: expectedFields.filter((f) => f.group === group),
    }))
    .filter((g) => g.fields.length > 0);
}

/** Continue is disabled until every required field (username / engagement_rate
 *  / avg_views) has a column mapped to it, and no two columns share a field. */
export function isMappingComplete(
  expectedFields: ExpectedField[],
  mapping: Record<string, string>,
): boolean {
  if (mappingClashes(mapping).length > 0) return false;
  const mappedKeys = new Set(Object.values(mapping).filter(Boolean));
  return expectedFields.filter((f) => f.required).every((f) => mappedKeys.has(f.key));
}

export function missingRequiredFields(
  expectedFields: ExpectedField[],
  mapping: Record<string, string>,
): ExpectedField[] {
  const mappedKeys = new Set(Object.values(mapping).filter(Boolean));
  return expectedFields.filter((f) => f.required && !mappedKeys.has(f.key));
}

/** In Unicode code points, like the BE's maxNameLength. */
export const MAX_NAME_LENGTH = 100;

/** Reports whether a deliverable-price cell holds something that reads as
 *  an amount but amountParse can't take, OR a negative number (no longer a
 *  valid "remove" convention — the literal word "remove" is, since a
 *  negative sign is one typo away from a valid positive price). Only cells
 *  containing a digit qualify — a purely verbal cell ("free" or "remove")
 *  or a percentage ("50%") is a documented "skip"/"remove" convention, not
 *  a typo. Mirrors the backend's looksLikeUnreadablePrice
 *  (import_review_rules.go) exactly. */
function looksLikeUnreadablePriceClient(key: string, raw: string, campaignPlatform: string | undefined): boolean {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed.endsWith('%')) return false;
  if (!campaignPlatform || !isOfferedOnPlatform(key, campaignPlatform)) return false;
  if (!/\p{Nd}/u.test(trimmed)) return false;
  const n = parseAmount(trimmed, 'price');
  return n === null || n < 0;
}

const HTTP_SCHEME_PATTERN = /^https?:\/\//i;
/** A bare "https://" — Go's url.Parse accepts it (empty host, empty path), so
 *  the BE reads it as a profile link with no handle. `new URL` throws on it. */
const SCHEME_ONLY_PATTERN = /^https?:\/\/$/i;
const DEFAULT_PROFILE_SCHEME = 'https://';

/** Known social hosts (after lowercasing and dropping a leading www./m.) and
 *  the platform each one belongs to — mirrors the BE's detectPlatformHost. A
 *  scheme-less cell is only read as a link when it names one of these. */
const PLATFORM_BY_HOST: ReadonlyMap<string, string> = new Map([
  ['instagram.com', 'instagram'],
  ['instagr.am', 'instagram'],
  ['youtube.com', 'youtube'],
  ['youtu.be', 'youtube'],
]);
const PLATFORMS_WITH_HOSTS: ReadonlySet<string> = new Set(PLATFORM_BY_HOST.values());

/** The first path segment of a same-platform URL that points at CONTENT
 *  (a post/reel/video), not a profile — mirrors the BE's
 *  nonProfilePathPrefixes (import_rules.go). */
const NON_PROFILE_PATH_PREFIXES = new Set([
  'p', 'reel', 'reels', 'tv', 'stories', 'explore', 'share', 'accounts', 'direct', '_u', // Instagram
  'watch', 'shorts', 'playlist', 'embed', 'results', 'live', 'feed', 'post', 'clip', 'hashtag', // YouTube
]);

/** YouTube URL forms whose first segment is a route word, not a handle —
 *  otherwise a creator literally called "channel", "c" or "user" would be
 *  imported. Mirrors the BE's legacyYouTubePathPrefixes. */
const LEGACY_YOUTUBE_PATH_PREFIXES = new Set(['channel', 'c', 'user']);

/** Filler words a sheet puts where a profile link is missing ("No" became
 *  @no). Only a bare cell counts; an explicit profile link is left alone.
 *  Mirrors the BE's placeholderHandles. */
const PLACEHOLDER_HANDLES = new Set(['no', 'na', 'n/a', 'nil', 'none', 'null', 'yes', 'tbd', 'pending']);

/** The words an optional metric cell may hold for "no value". Wider than the
 *  username filler list: a handle called "unknown" or "0" can be a real
 *  account (the profile lookup flags one that is not), a view count of
 *  "unknown" cannot be a number. */
const BLANK_METRIC_WORDS = new Set([
  'no', 'na', 'n/a', 'n.a', 'n.a.', 'nil', 'none', 'null', 'nan', 'yes', 'tbd',
  'pending', 'unknown', 'total', '0',
]);

/** A handle of only dots, underscores and hyphens is filler, not an account.
 *  Instagram handles are ASCII-only; YouTube handles may use letters and
 *  digits of any script. Mirrors the BE's hasHandleCharacter. */
const HAS_HANDLE_CHARACTER_BY_PLATFORM: ReadonlyMap<string, RegExp> = new Map([
  ['instagram', /[a-z0-9]/],
  ['youtube', /[\p{L}\p{N}]/u],
]);
const DEFAULT_HAS_HANDLE_CHARACTER = /[a-z0-9]/;

function hasHandleCharacter(handle: string, campaignPlatform: string | undefined): boolean {
  const pattern = HAS_HANDLE_CHARACTER_BY_PLATFORM.get(campaignPlatform ?? '') ?? DEFAULT_HAS_HANDLE_CHARACTER;
  return pattern.test(handle);
}

/** The error text a broken formula cell exports as. Mirrors the BE's excelErrorRE. */
const EXCEL_ERROR_PATTERN = /^#(n\/a|value!|div\/0!|ref!|name\?|null!|num!|spill!|calc!)$/;

/** An optional metric cell that says "no value": empty, a placeholder word
 *  ("N/A", "-", "TBD") or an Excel error. Mirrors the BE's isBlankMetricCell. */
export function isBlankMetricCell(value: string): boolean {
  const s = value.trim().toLowerCase();
  if (s === '' || s === '-' || s === '--' || s === '—') return true;
  return BLANK_METRIC_WORDS.has(s) || EXCEL_ERROR_PATTERN.test(s);
}

/** Mirrors the BE's handlePatternByPlatform. An unknown platform has no
 *  pattern and passes, so a new platform never flags every row by accident.
 *  The `u` flag counts {m,n} in code points, like Go counts runes: YouTube
 *  allows 3-30 letters, combining marks and digits from many scripts. */
const HANDLE_PATTERN_BY_PLATFORM: ReadonlyMap<string, RegExp> = new Map([
  ['instagram', /^[a-z0-9._]{1,30}$/],
  ['youtube', /^[\p{L}\p{M}\p{N}._-]{3,30}$/u],
]);

const PLATFORM_LABEL: ReadonlyMap<string, string> = new Map([
  ['instagram', 'Instagram'],
  ['youtube', 'YouTube'],
]);

/** What to enter in the identity cell, per platform: YouTube has channels and
 *  handles, not profiles and usernames. Mirrors the BE's profileURLGuidance. */
const GUIDANCE_BY_PLATFORM: ReadonlyMap<string, string> = new Map([
  ['instagram', 'Enter an Instagram profile URL or username'],
  ['youtube', 'Enter a YouTube channel URL or handle'],
]);
const DEFAULT_GUIDANCE = 'Enter a creator profile URL or username';
const CONTENT_LINK_GUIDANCE_SUFFIX = ', not a post or video URL.';
const GUIDANCE_SUFFIX = '.';

const LEGACY_YOUTUBE_LINK_MESSAGE = "Paste the channel's @handle link (youtube.com/@name).";

const METRIC_MESSAGE = 'Enter a number greater than 0.';
/** What a cell whose text starts with "=" says: a formula the file saved no
 *  result for, or a value typed with a leading "=" in a CSV or the grid. Mirrors
 *  the BE's uncachedFormulaMessage. */
export const UNCACHED_FORMULA_MESSAGE =
  'This is a spreadsheet formula, not a value. Type the value itself, or re-save the file in Excel or Google Sheets so the result is saved.';

/** A cell that starts with "=" and has something after it: formula text the
 *  server keeps for a formula it could not read a result for. */
function isUncachedFormulaText(value: string): boolean {
  const trimmed = trimSpace(value);
  return trimmed.length > 1 && trimmed.startsWith('=');
}
/** A comma in a metric cell is only ever thousands grouping (12,500 or
 *  1,25,000), so "4,5" is flagged instead of being read as 45. */
const DECIMAL_COMMA_MESSAGE = 'Write decimals with a dot (4.5, not 4,5). A comma can only separate thousands, like 12,500.';
/** Engagement is a percentage: above 100 is a typo ("985" for 9.85). Mirrors
 *  the BE's maxEngagementRate / engagementRangeMessage. */
const MAX_ENGAGEMENT_RATE = 100;
const ENGAGEMENT_RANGE_MESSAGE = 'Enter an engagement rate between 0 and 100.';

const MIN_FOLLOWERS = 0;
/** Days are plain digits only: "1k", "30 days" or "30.0" would otherwise be
 *  read as a number the admin may not have meant. Mirrors the BE's days rule. */
const PLAIN_DIGITS_PATTERN = /^\d+$/;
/** Tolerance for float noise from a suffix multiplier (1.1k -> 1100.0000000000002).
 *  Same value and `<=` comparison as the BE's wholeNumberTolerance, so a cell
 *  the grid accepts is never rejected on save (or the other way round). */
const WHOLE_NUMBER_TOLERANCE = 1e-6;
const FOLLOWERS_MESSAGE = `Enter a whole number, ${MIN_FOLLOWERS} or more.`;
const USAGE_RIGHTS_DAYS_MESSAGE = `Enter a whole number of days from ${MIN_USAGE_RIGHTS_DAYS} to ${MAX_USAGE_RIGHTS_DAYS}.`;
const USAGE_DAYS_WITHOUT_PRICE_MESSAGE = 'Add a Usage Rights price for these days.';

const NO_MAIN_DELIVERABLE_PRICE_MESSAGE = 'Price at least one main deliverable.';
/** The one payout model where a ₹0 deliverable is not a price: the BE's
 *  classifyDeliverableCell reads it as blank there and as a real (free /
 *  barter) price under every other model. */
const FIXED_FEE_PAYOUT_MODEL = 'fixed_fee';

interface ProfileUrlParts {
  hostname: string;
  pathname: string;
}

function trimChars(s: string, chars: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && chars.includes(s.charAt(start))) start += 1;
  while (end > start && chars.includes(s.charAt(end - 1))) end -= 1;
  return s.slice(start, end);
}

function stripHostPrefixes(host: string): string {
  return host.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
}

function schemelessHost(s: string): string {
  return stripHostPrefixes(s.split('/')[0] ?? '');
}

function withProfileScheme(s: string): string | null {
  if (HTTP_SCHEME_PATTERN.test(s)) return s;
  return PLATFORM_BY_HOST.has(schemelessHost(s)) ? `${DEFAULT_PROFILE_SCHEME}${s}` : null;
}

function parseUrlParts(s: string): ProfileUrlParts | null {
  if (SCHEME_ONLY_PATTERN.test(s)) return { hostname: '', pathname: '' };
  try {
    const url = new URL(s);
    return { hostname: url.hostname, pathname: url.pathname };
  } catch {
    // Unparseable: the BE treats it as a bare handle, so null means "not a link".
    return null;
  }
}

/** Mirrors the BE's common.ProfileURL: an http(s) URL, or a scheme-less link
 *  on a known social host ("instagram.com/x"). null means a bare handle. */
function profileURL(raw: string): ProfileUrlParts | null {
  const s = raw.trim();
  if (s === '') return null;
  const withScheme = withProfileScheme(s);
  if (withScheme === null) return null;
  return parseUrlParts(withScheme);
}

// URL.pathname stays percent-encoded while the BE reads the decoded path, so
// "%40Name" must be read as "@Name" here too. A malformed escape is kept as is.
function decodeSegment(seg: string): string {
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
}

function pathSegments(pathname: string): string[] {
  return trimChars(pathname, '/').split('/').map((seg) => decodeSegment(seg).trim());
}

/** The @-prefixed segment (YouTube handle form), else the first non-empty
 *  segment (Instagram / legacy YouTube form). */
function pickHandleSegment(pathname: string): string {
  const segments = pathSegments(pathname);
  return segments.find((seg) => seg.startsWith('@')) ?? segments.find((seg) => seg !== '') ?? '';
}

function firstPathSegment(pathname: string): string {
  return (pathSegments(pathname)[0] ?? '').toLowerCase();
}

function stripOneLeadingAt(s: string): string {
  return s.startsWith('@') ? s.slice(1) : s;
}

/** Mirrors the BE's NormalizeSocialHandle (internal/common/social_handle.go):
 *  a profile URL yields its @segment or first path segment; the result then
 *  loses one trailing "/", stray commas, a leading "@", trailing "," / "@"
 *  copy-paste artifacts, and is lowercased. */
export function extractHandleClient(raw: string, campaignPlatform?: string): string {
  const youtube = canonicalPlatform(campaignPlatform) === 'youtube';
  // YouTube handles use letters of any script: put the text in Unicode normal
  // form C so a precomposed and a decomposed spelling are one handle. The order
  // is NFC, then the dotted capital I, then lowercase, as on the backend.
  const cleaned = trimSpace(stripAllInvisibleChars(raw));
  const trimmed = youtube ? cleaned.normalize('NFC') : cleaned;
  if (trimmed === '') return '';
  const url = profileURL(trimmed);
  // A link's path is decoded once, by the URL parse; a bare YouTube handle
  // copied percent-encoded is decoded here, once.
  const picked = url ? pickHandleSegment(url.pathname) : youtube ? decodeBareHandle(trimmed) : trimmed;
  const withoutSlash = trimSpace(picked.endsWith('/') ? picked.slice(0, -1) : picked);
  const withoutAt = stripOneLeadingAt(trimChars(withoutSlash, ','));
  const handle = trimSpace(trimChars(withoutAt, ',@'))
    .replaceAll(TURKISH_DOTTED_CAPITAL_I, 'i')
    .toLowerCase();
  return youtube ? handle.normalize('NFC') : handle;
}

/** "İ" lowercases to "i" plus a combining dot in JavaScript but to a plain "i"
 *  on the backend; it is read as "i" before lowercasing so both agree. */
const TURKISH_DOTTED_CAPITAL_I = '\u0130';

/** Characters that would change what a decoded handle means: any control
 *  character (NUL included), any space, and / ? # @. */
const FORBIDDEN_DECODED_HANDLE = /[\p{Cc}\s\u0085/?#@]/u;

/** Decodes a percent-encoded bare handle once. The text comes back unchanged
 *  (and so still holding the "%", which no handle may) when an escape is
 *  malformed (decodeURIComponent throws), or the decoded handle holds a
 *  forbidden character, so the handle is then invalid rather than thrown on. */
function decodeBareHandle(raw: string): string {
  if (!raw.includes('%')) return raw;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return raw;
  }
  const body = stripOneLeadingAt(trimSpace(decoded));
  return FORBIDDEN_DECODED_HANDLE.test(body) ? raw : decoded;
}

/** A fullwidth letter, digit or punctuation mark (U+FF01 to U+FF5E) looks like
 *  its ASCII twin but is another character: a handle holding one is never the
 *  account it looks like. */
const FULLWIDTH_LATIN = /[\uFF01-\uFF5E]/;

function detectedPlatform(raw: string): string {
  const url = profileURL(raw);
  if (!url) return '';
  return PLATFORM_BY_HOST.get(stripHostPrefixes(url.hostname)) ?? '';
}

function isLink(raw: string): boolean {
  return profileURL(raw) !== null;
}

/** Mirrors the BE's classifyUsernamePlatform: a link must sit on one of the
 *  campaign platform's own hosts, so another platform's link, a link-in-bio
 *  page or a look-alike host (instagram.com.evil.com, studio.youtube.com) is
 *  a mismatch. A bare handle never is. A campaign platform with no known
 *  hosts keeps the older rule: only a recognised link for another platform
 *  is a mismatch. */
function isPlatformMismatch(raw: string, campaignPlatform: string): boolean {
  if (!isLink(raw)) return false;
  const detected = detectedPlatform(raw);
  if (detected === '') return PLATFORMS_WITH_HOSTS.has(campaignPlatform);
  return detected !== campaignPlatform;
}

/** youtu.be only serves video short links (youtu.be/<id>), never channels —
 *  so the video id would otherwise be read as a handle. Mirrors the BE's
 *  youTubeShortLinkHost. */
const YOUTUBE_SHORT_LINK_HOST = 'youtu.be';

function isYouTubeShortLinkHost(hostname: string): boolean {
  return hostname.toLowerCase().replace(/^www\./, '') === YOUTUBE_SHORT_LINK_HOST;
}

function isContentURL(raw: string): boolean {
  const url = profileURL(raw);
  if (!url) return false;
  if (isYouTubeShortLinkHost(url.hostname)) return true;
  return NON_PROFILE_PATH_PREFIXES.has(firstPathSegment(url.pathname));
}

function isLegacyYouTubeURL(raw: string): boolean {
  const url = profileURL(raw);
  if (!url) return false;
  if (detectedPlatform(raw) !== 'youtube') return false;
  return LEGACY_YOUTUBE_PATH_PREFIXES.has(firstPathSegment(url.pathname));
}

function profileURLGuidance(campaignPlatform: string | undefined, isContent = false): string {
  const guidance = GUIDANCE_BY_PLATFORM.get(campaignPlatform ?? '') ?? DEFAULT_GUIDANCE;
  return `${guidance}${isContent ? CONTENT_LINK_GUIDANCE_SUFFIX : GUIDANCE_SUFFIX}`;
}

function handleFormatError(handle: string, campaignPlatform: string | undefined): string | null {
  const pattern = campaignPlatform ? HANDLE_PATTERN_BY_PLATFORM.get(campaignPlatform) : undefined;
  if (!pattern || (!FULLWIDTH_LATIN.test(handle) && pattern.test(handle))) return null;
  return `Not a valid ${PLATFORM_LABEL.get(campaignPlatform ?? '') ?? 'creator'} handle.`;
}

function isPlaceholderHandle(raw: string): boolean {
  return !isLink(raw) && PLACEHOLDER_HANDLES.has(raw.trim().toLowerCase());
}

/** The single username rule for a sheet cell, in the order an admin should
 *  fix things — mirrors the BE's usernameFieldError: missing or placeholder,
 *  wrong platform, content link, legacy YouTube link, then handle format.
 *  campaignPlatform is omitted before the campaign has loaded; the
 *  platform-specific checks are then skipped and the server still catches
 *  them on submit. */
function usernameError(rawValue: string, campaignPlatform: string | undefined): string | null {
  const value = stripAllInvisibleChars(rawValue);
  const handle = extractHandleClient(value, campaignPlatform);
  if (handle === '' || isPlaceholderHandle(value) || !hasHandleCharacter(handle.toLowerCase(), campaignPlatform)) {
    return profileURLGuidance(campaignPlatform);
  }
  if (campaignPlatform && isPlatformMismatch(value, campaignPlatform)) return profileURLGuidance(campaignPlatform);
  if (isContentURL(value)) return profileURLGuidance(campaignPlatform, true);
  if (isLegacyYouTubeURL(value)) return LEGACY_YOUTUBE_LINK_MESSAGE;
  return handleFormatError(handle, campaignPlatform);
}

/** A whole number after the shared amount parser ("12k", "1,200" are fine),
 *  or null when the cell isn't a number or has a fractional part. */
function parseWholeNumber(value: string): number | null {
  const n = parseAmount(value.trim(), 'metric');
  if (n === null) return null;
  return Math.abs(n - Math.round(n)) <= WHOLE_NUMBER_TOLERANCE ? Math.round(n) : null;
}

/** What a number with a decimal comma looks like ("4,5", "4,5%", "1.234,56",
 *  "2,5k"); any other text keeps the field's own message. */
const NUMERIC_LOOKING_PATTERN = /^[\d.,\s]+[%kmb]?$/i;

/** The message for a metric cell the parser can't read: the comma rule when a
 *  number with a misplaced comma is the likely cause, the field's own message
 *  otherwise ("12,500 views" or "N/A, pending" are not decimal commas). */
function unreadableMetricMessage(value: string, fallback: string): string {
  const trimmed = trimSpace(stripAllInvisibleChars(value));
  const decimalComma = trimmed.includes(',') && NUMERIC_LOOKING_PATTERN.test(trimmed) && parseAmount(trimmed, 'metric') === null;
  return decimalComma ? DECIMAL_COMMA_MESSAGE : fallback;
}

function followersError(value: string): string | null {
  if (isBlankMetricCell(value)) return null;
  const n = parseWholeNumber(value);
  return n !== null && n >= MIN_FOLLOWERS ? null : unreadableMetricMessage(value, FOLLOWERS_MESSAGE);
}

function usageRightsDaysError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!PLAIN_DIGITS_PATTERN.test(trimmed)) return USAGE_RIGHTS_DAYS_MESSAGE;
  const days = Number(trimmed);
  const inRange = days >= MIN_USAGE_RIGHTS_DAYS && days <= MAX_USAGE_RIGHTS_DAYS;
  return inRange ? null : USAGE_RIGHTS_DAYS_MESSAGE;
}

/** Counts Unicode code points like the BE's utf8.RuneCountInString — `length`
 *  counts UTF-16 units, which would count every emoji twice. */
function codePointCount(value: string): number {
  return Array.from(value).length;
}

function nameError(value: string): string | null {
  if (codePointCount(value) <= MAX_NAME_LENGTH) return null;
  return `Enter ${MAX_NAME_LENGTH} characters or fewer.`;
}

/** Optional metric (Instagram): blank, a "no value" placeholder ("N/A",
 *  "#N/A") and any number the parser reads, 0 included, are fine — only
 *  unreadable text is an error. */
function optionalMetricError(value: string): string | null {
  if (isBlankMetricCell(value)) return null;
  return parseAmount(value.trim(), 'metric') === null ? unreadableMetricMessage(value, METRIC_MESSAGE) : null;
}

/** Mirrors the BE's engagement range check, which runs after the metric rule. */
function engagementError(value: string, required: boolean | undefined): string | null {
  const metric = metricError(value, required);
  if (metric) return metric;
  const n = parseAmount(value.trim(), 'metric');
  return n !== null && n > MAX_ENGAGEMENT_RATE ? ENGAGEMENT_RANGE_MESSAGE : null;
}

function requiredMetricError(value: string): string | null {
  const n = parseAmount(value, 'metric');
  return n !== null && n > 0 ? null : unreadableMetricMessage(value, METRIC_MESSAGE);
}

/** OWNER RULE: this only mirrors the BE's engagement_rate / avg_views rule
 *  (validateBatchRowValues) — it must agree with the server, never lead it. */
function metricError(value: string, required: boolean | undefined): string | null {
  if (required === false) return optionalMetricError(value);
  return requiredMetricError(value);
}

/** The platform every per-platform rule here keys on — trimmed and
 *  lowercased, like the BE's CanonicalSocialPlatform. A stored variant
 *  ("Instagram", "youtube ") would otherwise miss every lookup and flag valid
 *  links the server accepts. Undefined stays undefined (campaign not loaded). */
function canonicalPlatform(raw: string | undefined): string | undefined {
  return raw?.trim().toLowerCase();
}

/** Mirrors the BE's validateBatchRowValues for the fields it actually
 *  validates (import_review_rules.go) — the fields the import grid and the
 *  "Needs fixing" editor show a red/green border for. Returns null when the
 *  CURRENT draft value is valid, so a field flips from red to green live as
 *  the admin types, instead of only finding out after the server round-trip.
 *  Two BE-mirroring gates: campaignPlatform gates deliverable-key coverage the
 *  same way the BE's OfferedOnPlatform check does — omitted (e.g. before the
 *  campaign has loaded), deliverable cells are left unvalidated client-side
 *  (server still catches it on submit). `required` gates engagement_rate/
 *  avg_views the same way the BE's requireMetricsForPlatform does — explicitly
 *  false (e.g. Instagram, where these aren't required) accepts a blank cell
 *  and any readable number, 0 included, and flags only unreadable text, the
 *  one thing the server raises there. Omitted (unknown yet) behaves like the
 *  old, stricter default and still requires a value greater than 0.
 *  usage_rights_days only checks its own range here; the "days need a price"
 *  rule needs the whole row — see clientRowErrors. */
export function clientFieldError(key: string, value: string, rawCampaignPlatform?: string, required?: boolean): string | null {
  const campaignPlatform = canonicalPlatform(rawCampaignPlatform);
  if (isUncachedFormulaText(value)) return UNCACHED_FORMULA_MESSAGE;
  switch (key) {
    case USERNAME_KEY:
      return usernameError(value, campaignPlatform);
    case 'engagement_rate':
      return engagementError(value, required);
    case 'avg_views':
      return metricError(value, required);
    case 'first_name':
    case 'last_name':
      return nameError(value);
    case 'followers':
      return followersError(value);
    case USAGE_RIGHTS_DAYS_KEY:
      return usageRightsDaysError(value);
    default:
      return looksLikeUnreadablePriceClient(key, value, campaignPlatform)
        ? 'Enter a plain amount such as 12000 or 12k, or enter remove to clear this deliverable.'
        : null;
  }
}

function usageDaysNeedPrice(values: Record<string, string>): boolean {
  const days = values[USAGE_RIGHTS_DAYS_KEY] ?? '';
  const price = values[USAGE_RIGHTS_KEY] ?? '';
  return !isBlank(days) && isBlank(price);
}

/** What the row-level rules need to know beyond the row's own cells. */
export interface RowRuleContext {
  /** The row matches nobody in the campaign (`rosterMatch === null`). */
  isNewCreator: boolean;
  /** Unknown (not passed down yet) reads a 0 price as a price, like the BE
   *  does for every model but fixed_fee. */
  payoutModel: string | undefined;
}

function isMainDeliverableField(field: ExpectedField): boolean {
  return field.group === MAIN_DELIVERABLE_GROUP;
}

export function allowsFreeDeliverables(payoutModel: string | undefined): boolean {
  return payoutModel !== FIXED_FEE_PAYOUT_MODEL;
}

/** Mirrors the "set" outcome of the BE's classifyDeliverableCell: a readable
 *  amount above 0, or exactly 0 on any payout model but
 *  fixed fee. Blank, wordy, "remove" and negative cells set nothing. */
function setsDeliverablePrice(raw: string, payoutModel: string | undefined): boolean {
  const amount = parseAmount(raw, 'price');
  if (amount === null || amount < 0) return false;
  if (amount === 0) return allowsFreeDeliverables(payoutModel);
  return true;
}

/** The cell that carries "Price at least one main deliverable.": the first
 *  main-deliverable column, when a new creator's row prices none of them.
 *  Null when the rule is met or doesn't apply (an existing creator keeps its
 *  deliverables; a grid without main-deliverable columns can't price one). */
function unpricedMainDeliverableKey(
  values: Record<string, string>,
  expectedFields: ExpectedField[],
  context: RowRuleContext,
): string | null {
  if (!context.isNewCreator) return null;
  const mainFields = expectedFields.filter(isMainDeliverableField);
  const firstMainField = mainFields[0];
  if (firstMainField === undefined) return null;
  const priced = mainFields.some((field) => setsDeliverablePrice(values[field.key] ?? '', context.payoutModel));
  return priced ? null : firstMainField.key;
}

/** Live per-row validation for the import grid: every expected field through
 *  clientFieldError, plus the rules that span cells — usage days set with no
 *  Usage Rights price, and a new creator with no main deliverable priced. A
 *  cell that already fails its own check keeps that message. */
export function clientRowErrors(
  values: Record<string, string>,
  expectedFields: ExpectedField[],
  campaignPlatform: string,
  context: RowRuleContext,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of expectedFields) {
    const error = clientFieldError(field.key, values[field.key] ?? '', campaignPlatform, field.required);
    if (error) errors[field.key] = error;
  }
  const hasDaysField = expectedFields.some((f) => f.key === USAGE_RIGHTS_DAYS_KEY);
  if (hasDaysField && !errors[USAGE_RIGHTS_DAYS_KEY] && usageDaysNeedPrice(values)) {
    errors[USAGE_RIGHTS_DAYS_KEY] = USAGE_DAYS_WITHOUT_PRICE_MESSAGE;
  }
  const unpricedKey = unpricedMainDeliverableKey(values, expectedFields, context);
  if (unpricedKey !== null && !errors[unpricedKey]) {
    errors[unpricedKey] = NO_MAIN_DELIVERABLE_PRICE_MESSAGE;
  }
  return errors;
}

/** The handle the import will actually store, shown under a cell only when it
 *  differs from what the sheet had — so a pasted link visibly becomes a
 *  handle, and an already-clean handle shows nothing extra. */
export function handlePreview(raw: string, campaignPlatform?: string): string | null {
  const handle = extractHandleClient(raw, campaignPlatform);
  if (handle === '') return null;
  return handle === trimSpace(raw) ? null : handle;
}

const SKIPPED_AT_IMPORT: ImportBatchRow['dropReason'] = 'skipped_at_import';
const ADDED_RESOLUTIONS: readonly RosterResolutionAction[] = ['add'];
const UPDATED_RESOLUTIONS: readonly RosterResolutionAction[] = ['overwrite'];
const KEPT_RESOLUTIONS: readonly RosterResolutionAction[] = ['keep_existing', 'skip'];
const REJECTED_STATUS: CreatorNote['status'] = 'rejected';

/** Heading for a rejected row the server gave no reason for. */
export const REJECT_REASON_FALLBACK = 'No reason given';

/** The done screen's breakdown of one import (spec §5.4). */
export interface ImportDoneTally {
  added: number;
  updated: number;
  kept: number;
  skipped: number;
}

function isBlockingRow(row: ImportBatchRow): boolean {
  return isBlockingStatus(reviewRowStatus(row));
}

function isCommittedRow(row: ImportBatchRow): boolean {
  return !isDroppedRow(row) && !isBlockingRow(row);
}

// WHY a blocking row counts as skipped: Import stays disabled while anything
// blocks, so a row still blocking in the pre-commit snapshot can only have
// gone through "Skip N rows and import" — the server marked it
// skipped_at_import after the snapshot was taken, so the snapshot itself
// never carries that reason.
function isSkippedAtImport(row: ImportBatchRow): boolean {
  if (row.dropReason === SKIPPED_AT_IMPORT) return true;
  if (isDroppedRow(row)) return false;
  return isBlockingRow(row);
}

function hasResolution(resolutions: readonly RosterResolutionAction[]) {
  return (row: ImportBatchRow) => resolutions.includes(row.rosterResolution);
}

function rejectedRowNumbers(notes: CreatorNote[]): Set<number> {
  return new Set(notes.filter((note) => note.status === REJECTED_STATUS).map((note) => note.row));
}

type RowPredicate = (row: ImportBatchRow) => boolean;

function inRowNumbers(rowNumbers: ReadonlySet<number>): RowPredicate {
  return (row) => rowNumbers.has(row.rowNumber);
}

function notIn(predicate: RowPredicate): RowPredicate {
  return (row) => !predicate(row);
}

function either(a: RowPredicate, b: RowPredicate): RowPredicate {
  return (row) => a(row) || b(row);
}

/** Counts for the done screen from the rows as they were at commit. A row
 *  the server rejected at commit (a rejected creator note for its row
 *  number) is neither added nor updated — it is listed under "rows weren't
 *  imported" instead. A deflected row (a partial roster drift turned its
 *  overwrite into keep_existing server-side, after this snapshot) counts as
 *  kept, never updated. */
export function importDoneTally(
  rowsAtCommit: ImportBatchRow[],
  result: ImportResult,
  deflectedRows: readonly number[],
): ImportDoneTally {
  const rejected = inRowNumbers(rejectedRowNumbers(result.creator_notes));
  const deflected = inRowNumbers(new Set(deflectedRows));
  const committed = rowsAtCommit.filter(isCommittedRow);
  const imported = committed.filter(notIn(rejected)).filter(notIn(deflected));
  return {
    added: imported.filter(hasResolution(ADDED_RESOLUTIONS)).length,
    updated: imported.filter(hasResolution(UPDATED_RESOLUTIONS)).length,
    kept: committed.filter(either(hasResolution(KEPT_RESOLUTIONS), deflected)).length,
    skipped: rowsAtCommit.filter(isSkippedAtImport).length,
  };
}

/** The handles of the rows a partial roster drift kept at their current
 *  values, in row order — for the done screen's note. */
export function driftKeptHandles(rowsAtCommit: ImportBatchRow[], deflectedRows: readonly number[]): string[] {
  const deflected = inRowNumbers(new Set(deflectedRows));
  return rowsAtCommit
    .filter(deflected)
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .map((row) => row.usernameKey);
}

export interface RejectedReasonGroup {
  reason: string;
  notes: CreatorNote[];
}

function rejectReasonOf(note: CreatorNote): string {
  return note.reject_reason || REJECT_REASON_FALLBACK;
}

/** The rows the server rejected, grouped by reason in first-seen order. */
export function rejectedRowsByReason(notes: CreatorNote[]): RejectedReasonGroup[] {
  const groups = new Map<string, CreatorNote[]>();
  for (const note of notes) {
    if (note.status !== REJECTED_STATUS) continue;
    const reason = rejectReasonOf(note);
    groups.set(reason, [...(groups.get(reason) ?? []), note]);
  }
  return [...groups].map(([reason, grouped]) => ({ reason, notes: grouped }));
}

/** Every item the outreach batch has finished with, failed ones included; an
 *  older backend that omits the failed count reads as none failed. */
export function outreachSettledCount(batch: OutreachImportBatch): number {
  return batch.createdCount + batch.matchedCount + batch.skippedCount + (batch.failedCount ?? 0);
}
